/**
 * Inline editor for a concept's `user_tags`.
 *
 * Layout:
 *
 *   Domain (auto)
 *   [tag] [tag] [tag]
 *
 *   Your tags
 *   [tag ✕] [tag ✕]                       ← row 1, pills wrap freely
 *   [ + Add a tag…                ] [Add] ← row 2, dedicated input row
 *
 * Mutations are optimistic - the chip appears / disappears the moment
 * the user acts, with the parent React Query invalidation in
 * `usePatchUserTags` confirming on success. `onError` reverts.
 */

import { useEffect, useState } from 'react';
import { Loader2, Plus, Tag, X } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@ai-tutor/ui';
import { cn } from '@/lib/utils';
import { usePatchUserTags } from '@/hooks/mindspace/useRevision';

interface UserTagEditorProps {
  conceptId: string;
  domainTags: string[];
  userTags: string[];
  /** Called with the new user-tag list after each successful PATCH. */
  onChange?: (next: string[]) => void;
}

export function UserTagEditor({
  conceptId,
  domainTags,
  userTags,
  onChange,
}: UserTagEditorProps) {
  const [draft, setDraft] = useState('');
  // Optimistic local copy of `userTags`. Reflects what the user sees
  // *right now* - mutated synchronously on add/remove so chips update
  // without waiting for the server roundtrip. Re-synced from props
  // whenever the parent's React Query fetch lands a fresh list.
  const [optimisticTags, setOptimisticTags] = useState(userTags);
  useEffect(() => {
    setOptimisticTags(userTags);
  }, [userTags]);

  const patch = usePatchUserTags();

  const addTag = async () => {
    const tag = draft.trim().toLowerCase();
    if (!tag) return;
    if (optimisticTags.includes(tag)) {
      // Don't spam a network request just to tell the server we want
      // a duplicate. Toast lightly and reset.
      notify.info('Tag already added');
      setDraft('');
      return;
    }
    const before = optimisticTags;
    setOptimisticTags([...before, tag]);
    setDraft('');
    try {
      const res = await patch.mutateAsync({ conceptId, add: [tag] });
      // Server is authoritative - if its list disagrees with ours
      // (e.g. case-normalized differently), use the server view.
      setOptimisticTags(res.user_tags);
      onChange?.(res.user_tags);
    } catch (err: any) {
      setOptimisticTags(before);
      notify.error(err, "Couldn't add tag");
    }
  };

  const removeTag = async (tag: string) => {
    const before = optimisticTags;
    setOptimisticTags(before.filter(t => t !== tag));
    try {
      const res = await patch.mutateAsync({ conceptId, remove: [tag] });
      setOptimisticTags(res.user_tags);
      onChange?.(res.user_tags);
    } catch (err: any) {
      setOptimisticTags(before);
      notify.error(err, "Couldn't remove tag");
    }
  };

  const draftIsEmpty = draft.trim().length === 0;

  return (
    <div className='space-y-3'>
      {/* Domain tags - read-only context */}
      {domainTags.length > 0 && (
        <div>
          <div className='mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground'>
            Domain
            <span className='rounded-sm bg-muted px-1 py-0.5 text-xs font-normal text-muted-foreground'>
              auto
            </span>
          </div>
          <div className='flex flex-wrap gap-1'>
            {domainTags.map(tag => (
              <span
                key={tag}
                className='inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs text-foreground/80'
              >
                <Tag className='h-2.5 w-2.5 opacity-60' />
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* User tags - editable, two-row layout */}
      <div>
        <div className='mb-1.5 text-sm font-medium text-foreground'>
          Your tags
        </div>

        {/* Row 1: pills (only if any). Wraps freely so it never collides
            with the input row below. */}
        {optimisticTags.length > 0 && (
          <div className='mb-2 flex flex-wrap gap-1.5'>
            {optimisticTags.map(tag => (
              <span
                key={tag}
                className='dark:tint-brand inline-flex items-center gap-1 rounded-md bg-brand-100 px-1.5 py-0.5 text-xs font-medium text-brand-700'
              >
                {tag}
                <button
                  type='button'
                  aria-label={`Remove tag ${tag}`}
                  className='rounded-sm p-0.5 transition-colors hover:bg-brand-200 hover:text-brand-900 dark:hover:bg-primary/50 dark:hover:text-primary'
                  onClick={() => void removeTag(tag)}
                >
                  <X className='h-2.5 w-2.5' />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Row 2: dedicated input row. gap-2 keeps Add button breathing
            room; soft indigo ring on focus replaces the browser default. */}
        <form
          onSubmit={e => {
            e.preventDefault();
            void addTag();
          }}
          className='flex items-center gap-2'
        >
          <input
            type='text'
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder='Add a tag (e.g. exam-prep)'
            disabled={patch.isPending}
            className={cn(
              'flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground/60 disabled:opacity-60 max-sm:h-11 max-sm:px-3 max-sm:text-base',
              // `globals.css` applies `ring-2 ring-ring ring-offset-2`
              // to every focus-visible input via a base-layer rule -
              // that's the ugly halo. Need `!important` to beat it;
              // border color carries the focus indicator instead.
              '!outline-none focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 dark:focus:border-primary',
              'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0'
            )}
          />
          <Button
            type='submit'
            disabled={draftIsEmpty || patch.isPending}
            size='sm'
            className='h-7 flex-shrink-0 gap-1 bg-brand-600 px-2.5 text-xs text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-600/40 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary dark:disabled:bg-primary/40 max-sm:h-11 max-sm:px-3.5'
          >
            {patch.isPending ? (
              <Loader2 className='h-3 w-3 animate-spin' />
            ) : (
              <Plus className='h-3 w-3' />
            )}
            Add
          </Button>
        </form>
      </div>
    </div>
  );
}
